
import React, { useState, useEffect, useRef } from 'react';
import { User } from '../api/supabaseEntities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Label } from "../components/ui/label.jsx";
import { Switch } from "../components/ui/switch.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { AlertCircle, Mail, Phone, Save, Bell, Languages, Camera, Upload, X, Volume2, VolumeX, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { notificationService, updateNotificationSettings } from '../services/NotificationService.js';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog.jsx";

import { UploadFile } from '../api/integrations.js';

export default function UserProfile() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    preferences: {
      language: 'pt-BR',
      notifications: {
        email: true,
        push: true,
        sound: true,
        updates: true,
        reminders: true,
        newsletter: false
      }
    }
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    email_tickets: true,
    email_system_updates: true,
    email_reminders: true,
    email_newsletter: false,
    push_enabled: true,
    push_tickets: true,
    push_system_updates: true,
    push_reminders: true,
    sound_enabled: true,
    sound_volume: 70,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    // Limpar a stream da câmera ao fechar
    return () => {
      stopCamera();
    };
  }, []);

  const loadUserData = async () => {
    try {
      console.log('🔄 Carregando dados do usuário...');
      const userData = await User.getCurrentProfile();
      console.log('✅ Dados do usuário carregados:', userData);
      
      setUser(userData);
      setFormData({
        name: userData.name || userData.email || '',
        email: userData.email || '',
        preferences: {
          language: 'pt-BR',
          notifications: {
            email: true,
            push: true,
            sound: true,
            updates: true,
            reminders: true,
            newsletter: false,
            ...userData.preferences?.notifications
          },
          ...userData.preferences
        }
      });
      
      // Carregar configurações de notificação
      await loadNotificationSettings(userData.id);
      
      // Inicializar serviço de notificações se não estiver inicializado
      if (!notificationService.isInitialized && userData.id) {
        await notificationService.initialize(userData.id);
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do usuário:', error);
      navigate(createPageUrl('Dashboard'));
    }
    setIsLoading(false);
  };

  const loadNotificationSettings = async (userId) => {
    try {
      console.log('🔔 Carregando configurações de notificação...');
      
      // Carregar configurações via notificationService
      await notificationService.loadUserSettings(userId);
      
      // Atualizar estado local com as configurações carregadas
      if (notificationService.settings) {
        setNotificationSettings(prev => ({
          ...prev,
          ...notificationService.settings
        }));
      }
      
      console.log('✅ Configurações de notificação carregadas');
      
    } catch (error) {
      console.error('❌ Erro ao carregar configurações de notificação:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferenceChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: value
      }
    }));
  };

  const handleNotificationChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        notifications: {
          ...prev.preferences.notifications,
          [key]: value
        }
      }
    }));
  };

  const handleNotificationSettingChange = async (key, value) => {
    try {
      setIsSaving(true);
      
      // Atualizar estado local
      const newSettings = {
        ...notificationSettings,
        [key]: value
      };
      setNotificationSettings(newSettings);
      
      // Salvar no banco via serviço
      await updateNotificationSettings(user.id, { [key]: value });
      
      console.log('✅ Configuração de notificação atualizada:', { [key]: value });
      
      // Se for configuração de som, atualizar o serviço
      if (key === 'sound_enabled' || key === 'sound_volume') {
        notificationService.soundEnabled = newSettings.sound_enabled;
        notificationService.soundVolume = newSettings.sound_volume;
      }
      
    } catch (error) {
      console.error('❌ Erro ao atualizar configuração:', error);
      // Reverter mudança em caso de erro
      setNotificationSettings(prev => ({
        ...prev,
        [key]: notificationSettings[key]
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const testNotification = async (type = 'test') => {
    try {
      await notificationService.sendNotification({
        userId: user.id,
        type: type,
        title: 'Teste de Notificação',
        message: `Esta é uma notificação de teste do tipo "${type}". Sistema funcionando perfeitamente!`,
        channels: ['push'],
        sound: true,
        data: {
          test: true,
          timestamp: new Date().toISOString()
        }
      });
      
      console.log('✅ Notificação de teste enviada');
      
    } catch (error) {
      console.error('❌ Erro ao enviar notificação de teste:', error);
    }
  };

  const scheduleTestReminder = async () => {
    try {
      // Agendar lembrete para 1 minuto no futuro
      const scheduledFor = new Date(Date.now() + 60000).toISOString();
      
      await notificationService.scheduleReminder({
        userId: user.id,
        title: 'Lembrete de Teste',
        message: 'Este é um lembrete de teste agendado para demonstrar a funcionalidade.',
        scheduledFor: scheduledFor,
        reminderType: 'test',
        channels: ['push']
      });
      
      alert('Lembrete agendado para 1 minuto no futuro!');
      console.log('✅ Lembrete de teste agendado');
      
    } catch (error) {
      console.error('❌ Erro ao agendar lembrete:', error);
      alert('Erro ao agendar lembrete de teste.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      console.log('💾 Salvando dados do usuário:', formData);
      
      // Usar update() da entidade User
      await User.update(user.id, {
        name: formData.name,
        preferences: formData.preferences
      });
      
      console.log('✅ Dados salvos com sucesso!');
      await loadUserData(); // Recarregar dados do usuário
      
      // Disparar evento para notificar AuthGuard sobre atualização do perfil
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: { profileData: formData, userId: user.id }
      }));
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      alert('Erro ao atualizar perfil. Por favor, tente novamente.');
    }

    setIsSaving(false);
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsSaving(true);
      
             console.log('📤 Fazendo upload do arquivo selecionado...', {
         name: file.name,
         type: file.type,
         size: file.size
       });
       
       // Verificar usuário atual (sistema customizado)
       console.log('🔐 Usuário atual:', { 
         id: user?.id, 
         email: user?.email,
         name: user?.name
       });
      
             // Upload real do arquivo
       const timestamp = Date.now();
       const fileName = `avatars/${timestamp}-${file.name}`;
       console.log('📤 Iniciando upload para:', fileName);
       
       const uploadResult = await UploadFile(file, 'uploads', fileName);
      
      console.log('✅ Upload realizado com sucesso:', uploadResult);
      console.log('🔗 URL gerada pelo upload:', uploadResult?.url);
      console.log('📊 Detalhes do upload:', {
        bucket: 'uploads',
        path: fileName,
        size: file.size,
        type: file.type
      });
      
      // Atualizar dados do usuário com a nova URL do avatar
      await User.update(user.id, {
        avatar_url: uploadResult.url
      });
      
      // Recarregar dados do usuário
      await loadUserData();
      
      // Disparar evento para notificar AuthGuard sobre atualização do perfil
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: { avatarUrl: uploadResult.url, userId: user.id }
      }));
      
      console.log('✅ Avatar atualizado com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao atualizar avatar:', error);
      alert(`Erro ao atualizar foto: ${error.message || 'Por favor, tente novamente.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Funções para a câmera
  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user" }, 
        audio: false 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar a câmera:', error);
      setCameraError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Configurar canvas para o tamanho do vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Obter o contexto e desenhar o frame atual
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Converter para base64
    const imageData = canvas.toDataURL('image/jpeg');
    setCapturedImage(imageData);
  };

  const resetCapture = () => {
    setCapturedImage(null);
  };

  const handleUploadCapturedImage = async () => {
    if (!capturedImage) return;
    
    try {
      setIsSaving(true);
      
      // Converter base64 para blob de forma mais segura
      const base64Data = capturedImage.split(',')[1]; // Remove o prefixo data:image/jpeg;base64,
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      // Criar um arquivo para enviar com timestamp único
      const timestamp = Date.now();
      const file = new File([blob], `camera-capture-${timestamp}.jpg`, { 
        type: 'image/jpeg',
        lastModified: timestamp
      });
      
             console.log('📤 Fazendo upload da foto capturada...', {
         name: file.name,
         type: file.type,
         size: file.size
       });
       
       // Verificar usuário atual (sistema customizado)
       console.log('🔐 Usuário atual:', { 
         id: user?.id, 
         email: user?.email,
         name: user?.name
       });
      
       // Upload do arquivo - corrigida a chamada da função
       const fileName = `avatars/${timestamp}-${file.name}`;
       console.log('📷 Iniciando upload da foto capturada para:', fileName);
       
       const uploadResult = await UploadFile(file, 'uploads', fileName);
      
       console.log('✅ Upload da foto capturada realizado com sucesso:', uploadResult);
       console.log('🔗 URL gerada pelo upload da câmera:', uploadResult?.url);
       console.log('📊 Detalhes do upload da câmera:', {
         bucket: 'uploads',
         path: fileName,
         size: file.size,
         type: file.type,
         source: 'camera'
       });
      
       // Atualizar dados do usuário com a nova URL do avatar (usar user do estado, não do Supabase Auth)
       await User.update(user.id, {
         avatar_url: uploadResult.url
       });
      
      // Fechar diálogo e parar a câmera
      stopCamera();
      setShowCameraDialog(false);
      setCapturedImage(null);
      
      // Recarregar dados do usuário
      await loadUserData();
      
      // Disparar evento para notificar AuthGuard sobre atualização do perfil
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: { avatarUrl: uploadResult.url, userId: user.id }
      }));
      
      console.log('✅ Avatar atualizado com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao salvar imagem capturada:', error);
      alert(`Erro ao salvar a foto: ${error.message || 'Por favor, tente novamente.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const openCameraDialog = () => {
    setShowCameraDialog(true);
    setCapturedImage(null);
    setCameraError('');
    
    // Iniciar a câmera após um breve delay para garantir que o diálogo está aberto
    setTimeout(() => {
      startCamera();
    }, 300);
  };

  const closeCameraDialog = () => {
    stopCamera();
    setShowCameraDialog(false);
    setCapturedImage(null);
  };

  if (isLoading || !user) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Meu Perfil</h1>
        <p className="text-muted-foreground mt-1">Gerencie suas informações e preferências</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Foto de Perfil</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-4">
            <Avatar className="h-32 w-32">
              {user.avatar_url ? (
                <AvatarImage src={user.avatar_url} alt={user.name} />
              ) : null}
              <AvatarFallback className="text-3xl">
                {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || user.email?.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="relative"
                  disabled={isSaving}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isSaving ? 'Salvando...' : 'Carregar'}
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    disabled={isSaving}
                  />
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={openCameraDialog}
                  disabled={isSaving}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Câmera
                </Button>
              </div>
              
              {user.avatar_url && (
                <Button 
                  variant="ghost" 
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={async () => {
                    if (confirm('Tem certeza que deseja remover sua foto?')) {
                      try {
                        setIsSaving(true);
                        // Tentar null e string vazia
                        await User.update(user.id, { avatar_url: null });
                        // Recarregar e verificar
                        await loadUserData();
                        if (user.avatar_url) {
                          await User.update(user.id, { avatar_url: '' });
                          await loadUserData();
                        }
                        window.dispatchEvent(new CustomEvent('userProfileUpdated', {
                          detail: { avatarUrl: null, userId: user.id }
                        }));
                      } catch (error) {
                        console.error('❌ Erro ao remover avatar:', error);
                        alert('Erro ao remover foto. Por favor, tente novamente.');
                      } finally {
                        setIsSaving(false);
                      }
                    }
                  }}
                  disabled={isSaving}
                >
                  Remover Foto
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Tabs defaultValue="profile">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="profile">Perfil</TabsTrigger>
              <TabsTrigger value="notifications">Notificações</TabsTrigger>
              <TabsTrigger value="preferences">Preferências</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Pessoais</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Digite seu nome"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          value={formData.email}
                          disabled
                          type="email"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Função</Label>
                        <Input
                          value={user.role === 'admin' ? 'Administrador' : 
                                user.role === 'client' ? 'Cliente' : 
                                user.role === 'manager' ? 'Gerente' : 
                                user.role === 'support' ? 'Suporte' : 
                                user.role}
                          disabled
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Empresa</Label>
                        <Input
                          value={user.company_id ? 'Empresa vinculada' : 'Nenhuma empresa'}
                          disabled
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={isSaving}>
                        <Save className="mr-2 h-4 w-4" />
                        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="notifications" className="mt-6">
              <div className="space-y-6">
                {/* Configurações Principais */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Configurações de Notificação
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Email */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-base font-medium">📧 Notificações por Email</Label>
                          <p className="text-sm text-muted-foreground">
                            Habilitar todas as notificações por email
                          </p>
                        </div>
                        <Switch
                          checked={notificationSettings.email_enabled}
                          onCheckedChange={(checked) => handleNotificationSettingChange('email_enabled', checked)}
                          disabled={isSaving}
                        />
                      </div>
                      
                      {notificationSettings.email_enabled && (
                        <div className="ml-6 space-y-3 p-4 bg-muted/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Chamados</Label>
                            <Switch
                              checked={notificationSettings.email_tickets}
                              onCheckedChange={(checked) => handleNotificationSettingChange('email_tickets', checked)}
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Atualizações do Sistema</Label>
                            <Switch
                              checked={notificationSettings.email_system_updates}
                              onCheckedChange={(checked) => handleNotificationSettingChange('email_system_updates', checked)}
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Lembretes</Label>
                            <Switch
                              checked={notificationSettings.email_reminders}
                              onCheckedChange={(checked) => handleNotificationSettingChange('email_reminders', checked)}
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Newsletter Mensal</Label>
                            <Switch
                              checked={notificationSettings.email_newsletter}
                              onCheckedChange={(checked) => handleNotificationSettingChange('email_newsletter', checked)}
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Push Notifications */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-base font-medium">🔔 Notificações Push</Label>
                          <p className="text-sm text-muted-foreground">
                            Receber notificações no navegador
                          </p>
                        </div>
                        <Switch
                          checked={notificationSettings.push_enabled}
                          onCheckedChange={(checked) => handleNotificationSettingChange('push_enabled', checked)}
                          disabled={isSaving}
                        />
                      </div>
                      
                      {notificationSettings.push_enabled && (
                        <div className="ml-6 space-y-3 p-4 bg-muted/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Chamados</Label>
                            <Switch
                              checked={notificationSettings.push_tickets}
                              onCheckedChange={(checked) => handleNotificationSettingChange('push_tickets', checked)}
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Atualizações do Sistema</Label>
                            <Switch
                              checked={notificationSettings.push_system_updates}
                              onCheckedChange={(checked) => handleNotificationSettingChange('push_system_updates', checked)}
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Lembretes</Label>
                            <Switch
                              checked={notificationSettings.push_reminders}
                              onCheckedChange={(checked) => handleNotificationSettingChange('push_reminders', checked)}
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Som */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-base font-medium flex items-center gap-2">
                            {notificationSettings.sound_enabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            Alertas Sonoros
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Sons para novas notificações
                          </p>
                        </div>
                        <Switch
                          checked={notificationSettings.sound_enabled}
                          onCheckedChange={(checked) => handleNotificationSettingChange('sound_enabled', checked)}
                          disabled={isSaving}
                        />
                      </div>
                      
                      {notificationSettings.sound_enabled && (
                        <div className="ml-6 space-y-3 p-4 bg-muted/30 rounded-lg">
                          <div className="space-y-2">
                            <Label className="text-sm">Volume: {notificationSettings.sound_volume}%</Label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={notificationSettings.sound_volume}
                              onChange={(e) => handleNotificationSettingChange('sound_volume', parseInt(e.target.value))}
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Horário de Silêncio */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      🌙 Horário de Silêncio
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Início</Label>
                        <Input
                          type="time"
                          value={notificationSettings.quiet_hours_start}
                          onChange={(e) => handleNotificationSettingChange('quiet_hours_start', e.target.value)}
                          disabled={isSaving}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fim</Label>
                        <Input
                          type="time"
                          value={notificationSettings.quiet_hours_end}
                          onChange={(e) => handleNotificationSettingChange('quiet_hours_end', e.target.value)}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Durante este período, notificações push e sons serão silenciados
                    </p>
                  </CardContent>
                </Card>

                {/* Testes e Ações */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      Testes & Configurações
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => testNotification('default')}
                        disabled={isSaving}
                      >
                        🔔 Testar Push
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => testNotification('success')}
                        disabled={isSaving}
                      >
                        ✅ Testar Som
                      </Button>
                      <Button
                        variant="outline"
                        onClick={scheduleTestReminder}
                        disabled={isSaving}
                      >
                        ⏰ Testar Lembrete
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if ('Notification' in window) {
                            Notification.requestPermission().then(permission => {
                              alert(`Permissão: ${permission}`);
                            });
                          }
                        }}
                        disabled={isSaving}
                      >
                        🔐 Solicitar Permissão
                      </Button>
                    </div>
                    
                    <div className="p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
                      <strong>Dica:</strong> Use os botões de teste para verificar se as notificações estão funcionando corretamente.
                      O teste de lembrete será enviado em 1 minuto.
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="preferences" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Configurações do Sistema</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      


                      
                      <div className="space-y-2">
                        <Label>Idioma</Label>
                        <select
                          value={formData.preferences.language}
                          onChange={(e) => handlePreferenceChange('language', e.target.value)}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="pt-BR">Português (Brasil)</option>
                          <option value="en">English</option>
                          <option value="es">Español</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSubmit} disabled={isSaving}>
                        <Save className="mr-2 h-4 w-4" />
                        {isSaving ? 'Salvando...' : 'Salvar Configurações'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Diálogo da câmera */}
      <Dialog open={showCameraDialog} onOpenChange={closeCameraDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tirar Foto</DialogTitle>
            <DialogDescription>
              Use a câmera para capturar uma nova foto de perfil ou recapture se necessário.
            </DialogDescription>
          </DialogHeader>

          {cameraError ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              {cameraError}
            </div>
          ) : capturedImage ? (
            <div className="flex flex-col items-center">
              <img 
                src={capturedImage} 
                alt="Foto capturada" 
                className="max-h-[300px] rounded-md" 
              />
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={resetCapture}>
                  <X className="h-4 w-4 mr-2" />
                  Recapturar
                </Button>
                <Button 
                  onClick={handleUploadCapturedImage}
                  disabled={isSaving}
                >
                  {isSaving ? "Salvando..." : "Usar Foto"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="relative w-full max-h-[300px] overflow-hidden rounded-md bg-black mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto"
                />
              </div>
              <Button onClick={capturePhoto}>
                <Camera className="h-4 w-4 mr-2" />
                Capturar Foto
              </Button>
            </div>
          )}

          <canvas ref={canvasRef} className="hidden" />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={closeCameraDialog}>
                Cancelar
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
